import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { workSchedulesAPI, usersAPI, roomsAPI, ordersAPI, establishmentsAPI, commonSpacesAPI } from '../../services/api';
import { FiDownload, FiSave, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import { USER_ROLES } from '../../utils/constants';
import { SkeletonTable } from '../../design-system';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ScheduleManagement = () => {
  const { addNotification } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Receptionist can only view, not edit
  const isReadOnly = user?.role === USER_ROLES.RECEPTIONIST;
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Show schedule colors option from establishment
  const [showScheduleColors, setShowScheduleColors] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState(USER_ROLES.CLEANING_STAFF);

  // All users by role (excluding establishment admins)
  const [cleaningStaff, setCleaningStaff] = useState([]);
  const [receptionists, setReceptionists] = useState([]);
  const [managers, setManagers] = useState([]);

  const [schedules, setSchedules] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [commonSpaces, setCommonSpaces] = useState([]);
  const [orders, setOrders] = useState([]);

  // Color schedule data (when showScheduleColors is enabled)
  const [colorData, setColorData] = useState(null);

  // Get employees for current tab
  const employees = activeTab === USER_ROLES.CLEANING_STAFF ? cleaningStaff :
                    activeTab === USER_ROLES.RECEPTIONIST ? receptionists :
                    managers;

  // Date selection
  const today = new Date();
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1); // 1-12

  // Schedule data (keyed by employee_id and date)
  const [scheduleData, setScheduleData] = useState({});
  const [initialScheduleData, setInitialScheduleData] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Keyboard navigation
  const [focusedCell, setFocusedCell] = useState(null); // { weekIndex, employeeIndex, dayIndex }
  const tableRefs = useRef({}); // Multiple tables, one per week

  // Navigation warning
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  useEffect(() => {
    loadData();
  }, [selectedYear, selectedMonth]);

  // Detect changes in schedule data
  useEffect(() => {
    const hasChanges = JSON.stringify(scheduleData) !== JSON.stringify(initialScheduleData);
    setHasUnsavedChanges(hasChanges);
  }, [scheduleData, initialScheduleData]);

  // Auto-focus on the focused cell
  useEffect(() => {
    if (focusedCell && tableRefs.current[focusedCell.weekIndex]) {
      const { weekIndex, employeeIndex, dayIndex } = focusedCell;
      const cellElement = tableRefs.current[weekIndex].querySelector(
        `[data-week="${weekIndex}"][data-employee="${employeeIndex}"][data-day="${dayIndex}"]`
      );
      if (cellElement) {
        cellElement.focus();
      }
    }
  }, [focusedCell]);

  // Warn before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Custom navigation blocker using click interceptor on navigation links
  useEffect(() => {
    const handleClick = (e) => {
      // Check if click was on a navigation link
      const target = e.target.closest('a[href], button[type="button"]');

      if (!target) return;

      // Check if it's a navigation link (not external)
      const href = target.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }

      // If there are unsaved changes, prevent navigation and show warning
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.stopPropagation();

        // Show custom modal dialog
        setPendingNavigation(href);
        setShowNavigationWarning(true);
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [hasUnsavedChanges, navigate]);

  const loadData = async () => {
    try {
      setIsLoading(true);

      // Load establishment to get show_schedule_colors
      let colorsEnabled = false;
      if (user?.establishment_id) {
        try {
          const establishment = await establishmentsAPI.getById(user.establishment_id);
          colorsEnabled = establishment?.show_schedule_colors || false;
          setShowScheduleColors(colorsEnabled);
        } catch (err) {
          console.error('Error loading establishment:', err);
          // Default to no colors if establishment can't be loaded
          setShowScheduleColors(false);
        }
      }

      // Load employees for all roles (excluding establishment admins)
      const [cleaningStaffData, allUsers] = await Promise.all([
        usersAPI.getCleaningStaff(),
        usersAPI.getAll()
      ]);

      setCleaningStaff(cleaningStaffData);

      // Filter receptionists and managers from all users
      setReceptionists(allUsers.filter(u => u.role === USER_ROLES.RECEPTIONIST));
      setManagers(allUsers.filter(u => u.role === USER_ROLES.MANAGER));

      // Load schedules for selected month
      const schedulesData = await workSchedulesAPI.getByMonth(selectedYear, selectedMonth);
      setSchedules(schedulesData);

      // Load rooms and common spaces to calculate cleaning time
      const [roomsData, commonSpacesData] = await Promise.all([
        roomsAPI.getAll(),
        commonSpacesAPI.getAll()
      ]);
      setRooms(roomsData);
      setCommonSpaces(commonSpacesData || []);

      // Load orders for the month to get room counts
      const firstDay = new Date(selectedYear, selectedMonth - 1, 1);
      const lastDay = new Date(selectedYear, selectedMonth, 0);
      const ordersData = await ordersAPI.getAll({
        start_date: dateToLocalISO(firstDay),
        end_date: dateToLocalISO(lastDay)
      });
      setOrders(ordersData || []);

      // Load color schedule data if enabled
      if (colorsEnabled) {
        try {
          const colorScheduleData = await workSchedulesAPI.getColorSchedule(selectedYear, selectedMonth);
          setColorData(colorScheduleData);
        } catch (err) {
          console.error('Error loading color schedule:', err);
          setColorData(null);
        }
      } else {
        setColorData(null);
      }

      // Build schedule data structure
      const dataMap = {};
      schedulesData.forEach(schedule => {
        const key = `${schedule.employee_id}_${schedule.date}`;
        dataMap[key] = {
          id: schedule.id,
          hours: schedule.hours,
          shift: schedule.shift || '',
          notes: schedule.notes || ''
        };
      });
      setScheduleData(dataMap);
      setInitialScheduleData(JSON.parse(JSON.stringify(dataMap))); // Deep copy for comparison
      setHasUnsavedChanges(false);

    } catch (error) {
      console.error('Error loading data:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar los datos del cuadrante'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviousMonth = () => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => () => {
        if (selectedMonth === 1) {
          setSelectedMonth(12);
          setSelectedYear(selectedYear - 1);
        } else {
          setSelectedMonth(selectedMonth - 1);
        }
      });
      setShowNavigationWarning(true);
    } else {
      if (selectedMonth === 1) {
        setSelectedMonth(12);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    }
  };

  const handleNextMonth = () => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => () => {
        if (selectedMonth === 12) {
          setSelectedMonth(1);
          setSelectedYear(selectedYear + 1);
        } else {
          setSelectedMonth(selectedMonth + 1);
        }
      });
      setShowNavigationWarning(true);
    } else {
      if (selectedMonth === 12) {
        setSelectedMonth(1);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const confirmNavigation = () => {
    setShowNavigationWarning(false);
    setHasUnsavedChanges(false);

    if (pendingNavigation) {
      if (typeof pendingNavigation === 'function') {
        // It's a month change function
        pendingNavigation();
      } else if (typeof pendingNavigation === 'string') {
        // It's a route navigation
        setTimeout(() => {
          navigate(pendingNavigation);
        }, 0);
      }
      setPendingNavigation(null);
    }
  };

  const cancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
  };

  const handleTabChange = (newTab) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => () => setActiveTab(newTab));
      setShowNavigationWarning(true);
    } else {
      setActiveTab(newTab);
    }
  };

  const handleHoursChange = (employeeId, date, hours) => {
    const key = `${employeeId}_${date}`;
    setScheduleData(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        hours: parseFloat(hours) || 0
      }
    }));
  };

  const handleShiftChange = (employeeId, weekDays, shift) => {
    // Update shift for all days of the week for this employee
    setScheduleData(prev => {
      const updated = { ...prev };
      weekDays.forEach(day => {
        const dateStr = dateToLocalISO(day);
        const key = `${employeeId}_${dateStr}`;
        const isCurrentMonth = day.getMonth() === selectedMonth - 1;

        if (isCurrentMonth) {
          updated[key] = {
            ...updated[key],
            shift: shift
          };
        }
      });
      return updated;
    });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Build schedules array from scheduleData
      const schedulesToSave = [];
      Object.entries(scheduleData).forEach(([key, data]) => {
        if (data.hours > 0) {
          const [employeeId, date] = key.split('_');
          schedulesToSave.push({
            establishment_id: user.establishment_id,
            employee_id: parseInt(employeeId),
            date: date,
            hours: data.hours,
            shift: data.shift || '',
            notes: data.notes || ''
          });
        }
      });

      await workSchedulesAPI.bulkCreateOrUpdate(schedulesToSave);

      addNotification({
        type: 'success',
        message: 'Cuadrantes guardados exitosamente'
      });

      // Reload data
      await loadData();

    } catch (error) {
      console.error('Error saving schedules:', error);
      addNotification({
        type: 'error',
        message: 'Error al guardar los cuadrantes'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const pdfBlob = await workSchedulesAPI.downloadPDF(selectedYear, selectedMonth);

      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      const monthNames = [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
      ];
      link.setAttribute('download', `cuadrante_${monthNames[selectedMonth - 1]}_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      addNotification({
        type: 'success',
        message: 'PDF descargado exitosamente'
      });

    } catch (error) {
      console.error('Error downloading PDF:', error);
      addNotification({
        type: 'error',
        message: 'Error al descargar el PDF'
      });
    }
  };

  // Keyboard navigation helpers
  const navigateToCell = useCallback((weekIndex, employeeIndex, dayIndex, weeks) => {
    // Validate indices
    if (weekIndex < 0 || weekIndex >= weeks.length) return;
    if (employeeIndex < 0 || employeeIndex >= employees.length) return;
    if (dayIndex < 0 || dayIndex >= 7) return;

    // Check if the day is in the current month
    const day = weeks[weekIndex][dayIndex];
    const isCurrentMonth = day.getMonth() === selectedMonth - 1;
    if (!isCurrentMonth) return;

    setFocusedCell({ weekIndex, employeeIndex, dayIndex });
  }, [employees.length, selectedMonth]);

  const navigateTab = useCallback((weekIndex, employeeIndex, dayIndex, direction, weeks) => {
    // Move to next/previous editable cell
    let newWeekIndex = weekIndex;
    let newEmployeeIndex = employeeIndex;
    let newDayIndex = dayIndex + direction;

    // Handle day overflow/underflow
    while (newDayIndex < 0 || newDayIndex >= 7) {
      if (newDayIndex < 0) {
        // Move to previous employee, last day
        newEmployeeIndex--;
        newDayIndex = 6;
      } else {
        // Move to next employee, first day
        newEmployeeIndex++;
        newDayIndex = 0;
      }
    }

    // Handle employee overflow/underflow
    while (newEmployeeIndex < 0 || newEmployeeIndex >= employees.length) {
      if (newEmployeeIndex < 0) {
        // Move to previous week, last employee
        newWeekIndex--;
        newEmployeeIndex = employees.length - 1;
      } else {
        // Move to next week, first employee
        newWeekIndex++;
        newEmployeeIndex = 0;
      }

      if (newWeekIndex < 0 || newWeekIndex >= weeks.length) {
        // Can't navigate further
        return;
      }
    }

    // Check if the target day is in current month
    const day = weeks[newWeekIndex][newDayIndex];
    const isCurrentMonth = day.getMonth() === selectedMonth - 1;

    if (isCurrentMonth) {
      navigateToCell(newWeekIndex, newEmployeeIndex, newDayIndex, weeks);
    } else {
      // Skip this cell and continue navigation
      navigateTab(newWeekIndex, newEmployeeIndex, newDayIndex, direction, weeks);
    }
  }, [employees.length, selectedMonth, navigateToCell]);

  const handleKeyDown = useCallback((e, weekIndex, employeeIndex, dayIndex, weeks) => {
    const { key, shiftKey } = e;

    // Tab navigation
    if (key === 'Tab') {
      e.preventDefault();
      const direction = shiftKey ? -1 : 1;
      navigateTab(weekIndex, employeeIndex, dayIndex, direction, weeks);
      return;
    }

    // Arrow navigation
    if (key === 'ArrowUp') {
      e.preventDefault();
      navigateToCell(weekIndex, employeeIndex - 1, dayIndex, weeks);
      return;
    }

    if (key === 'ArrowDown') {
      e.preventDefault();
      navigateToCell(weekIndex, employeeIndex + 1, dayIndex, weeks);
      return;
    }

    if (key === 'ArrowLeft') {
      e.preventDefault();
      navigateToCell(weekIndex, employeeIndex, dayIndex - 1, weeks);
      return;
    }

    if (key === 'ArrowRight') {
      e.preventDefault();
      navigateToCell(weekIndex, employeeIndex, dayIndex + 1, weeks);
      return;
    }
  }, [navigateToCell, navigateTab]);

  const handleInputFocus = (e) => {
    // Select all text when focusing an input
    e.target.select();
  };

  // Función para formatear horas decimales a formato HH:MM
  const formatHours = (decimalHours) => {
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  // Helper to convert local date to ISO string without timezone conversion
  const dateToLocalISO = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper to get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  // Helper to get weeks in month
  const getWeeksInMonth = () => {
    const firstDay = new Date(selectedYear, selectedMonth - 1, 1);
    const lastDay = new Date(selectedYear, selectedMonth, 0);
    const weeks = [];

    let currentWeekStart = new Date(firstDay);
    // Adjust to Monday
    const dayOfWeek = currentWeekStart.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    currentWeekStart.setDate(currentWeekStart.getDate() + daysToMonday);

    while (currentWeekStart <= lastDay) {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const weekDays = [];
      for (let i = 0; i < 7; i++) {
        const day = new Date(currentWeekStart);
        day.setDate(day.getDate() + i);
        // Siempre agregar el día, incluso si no pertenece al mes
        weekDays.push(day);
      }

      // Solo agregar la semana si al menos un día pertenece al mes
      const hasMonthDays = weekDays.some(day => day.getMonth() === selectedMonth - 1);
      if (hasMonthDays) {
        weeks.push(weekDays);
      }

      currentWeekStart.setDate(currentWeekStart.getDate() + 7);
    }

    return weeks;
  };

  // Helper to count rooms for a specific date
  const getRoomsForDate = (date) => {
    const dateStr = dateToLocalISO(date);
    return orders.filter(order => order.date === dateStr).length;
  };

  // Helper to calculate estimated hours for a date
  const getEstimatedHours = (date) => {
    const dateStr = dateToLocalISO(date);
    const ordersForDate = orders.filter(order => order.date === dateStr);

    let totalMinutes = 0;
    ordersForDate.forEach(order => {
      if (order.room_id) {
        const room = rooms.find(r => r.id === order.room_id);
        if (room && room.cleaning_time_minutes) {
          totalMinutes += room.cleaning_time_minutes;
        }
      } else if (order.common_space_id) {
        const space = commonSpaces.find(s => s.id === order.common_space_id);
        if (space && space.cleaning_time_minutes) {
          totalMinutes += space.cleaning_time_minutes;
        }
      }
    });

    return totalMinutes / 60;
  };

  // Helper to calculate total staff hours for a date
  const getStaffHoursForDate = (date) => {
    const dateStr = dateToLocalISO(date);
    let totalHours = 0;

    employees.forEach(employee => {
      const key = `${employee.id}_${dateStr}`;
      const hours = scheduleData[key]?.hours || 0;
      totalHours += hours;
    });

    return totalHours;
  };

  // Helper to get staffing status color for a date
  const getStaffingStatusColor = (date) => {
    const estimatedHours = parseFloat(getEstimatedHours(date));
    const staffHours = getStaffHoursForDate(date);
    const difference = staffHours - estimatedHours;

    if (difference < -0.5) {
      // Faltan horas de personal (rojo)
      return 'bg-red-100 text-red-800 border-red-200';
    } else if (difference > 0.5) {
      // Sobran horas de personal (azul)
      return 'bg-blue-100 text-blue-800 border-blue-200';
    } else {
      // Equilibrado (gris normal)
      return 'text-gray-700';
    }
  };

  // Helper to get colors for an employee on a specific date
  const getEmployeeColorsForDate = (employeeId, dateStr) => {
    if (!colorData || !colorData.employees) return null;
    const employeeData = colorData.employees.find(e => e.employee_id === employeeId);
    if (!employeeData) return null;
    return employeeData.schedule[dateStr] || null;
  };

  // Render color stripe for a cell
  const renderColorStripe = (colors) => {
    if (!colors || colors.length === 0) return null;

    if (colors.length === 1) {
      return (
        <div
          className="absolute bottom-0 left-0 right-0 h-1.5 rounded-b"
          style={{ backgroundColor: colors[0].color }}
          title={colors[0].name}
        />
      );
    }

    // Multiple colors - divide equally
    const stripeWidth = 100 / colors.length;
    const gradient = colors.map((c, i) =>
      `${c.color} ${i * stripeWidth}%, ${c.color} ${(i + 1) * stripeWidth}%`
    ).join(', ');

    return (
      <div
        className="absolute bottom-0 left-0 right-0 h-1.5 rounded-b"
        style={{ background: `linear-gradient(to right, ${gradient})` }}
        title={colors.map(c => c.name).join(' + ')}
      />
    );
  };

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const weeks = getWeeksInMonth();

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with month selector - SIEMPRE VISIBLE */}
      <div className="bg-white rounded-lg shadow p-3 sm:p-4">
        <div className="space-y-3 sm:space-y-0">
          {/* Title - Always on top */}
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            Gestión de Cuadrantes
          </h1>

          {/* Controls row - Month selector and actions */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            {/* Month selector */}
            <div className="flex items-center justify-center sm:justify-start space-x-3 sm:space-x-4">
              <button
                onClick={handlePreviousMonth}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                aria-label="Mes anterior"
                disabled={isLoading}
              >
                <FiChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
              <h2 className="text-lg sm:text-xl font-semibold text-center min-w-0">
                {monthNames[selectedMonth - 1]} {selectedYear}
              </h2>
              <button
                onClick={handleNextMonth}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                aria-label="Mes siguiente"
                disabled={isLoading}
              >
                <FiChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-stretch sm:justify-end space-x-2 sm:space-x-3">
              <button
                onClick={handleDownloadPDF}
                className="btn btn-secondary flex items-center justify-center space-x-2 flex-1 sm:flex-none"
                disabled={isLoading}
              >
                <FiDownload className="h-4 w-4" />
                <span>Descargar PDF</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs for role selection */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 px-4" aria-label="Tabs">
            <button
              onClick={() => handleTabChange(USER_ROLES.CLEANING_STAFF)}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === USER_ROLES.CLEANING_STAFF
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              Personal de Limpieza
              {cleaningStaff.length > 0 && (
                <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-gray-100 text-gray-900">
                  {cleaningStaff.length}
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabChange(USER_ROLES.RECEPTIONIST)}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === USER_ROLES.RECEPTIONIST
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              Recepcionistas
              {receptionists.length > 0 && (
                <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-gray-100 text-gray-900">
                  {receptionists.length}
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabChange(USER_ROLES.MANAGER)}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === USER_ROLES.MANAGER
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              Encargados
              {managers.length > 0 && (
                <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-gray-100 text-gray-900">
                  {managers.length}
                </span>
              )}
            </button>
          </nav>
        </div>
      </div>


      {/* Hours table - Always shown */}
      {/* Weekly tables con Skeleton */}
      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Show 4 skeleton tables (typical month has 4-5 weeks) */}
          {[1, 2, 3, 4].map((weekNum) => (
            <SkeletonTable
              key={weekNum}
              rows={8}
              columns={8}
              showActions={false}
              animation="shimmer"
              duration={1.5}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {weeks.map((weekDays, weekIndex) => {
          const weekStart = weekDays[0];
          const weekEnd = weekDays[weekDays.length - 1];

          // Calculate weekly totals
          let weeklyRoomsTotal = 0;
          let weeklyEstimatedHoursTotal = 0;

          weekDays.forEach(day => {
            const isCurrentMonth = day.getMonth() === selectedMonth - 1;
            if (isCurrentMonth) {
              weeklyRoomsTotal += getRoomsForDate(day);
              weeklyEstimatedHoursTotal += parseFloat(getEstimatedHours(day));
            }
          });

          return (
            <div key={weekIndex} className="bg-white rounded-lg shadow p-3 sm:p-4">
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
                Semana {weekIndex + 1} ({weekStart.getDate()}/{weekStart.getMonth() + 1} - {weekEnd.getDate()}/{weekEnd.getMonth() + 1})
              </h3>

              <div className="overflow-x-auto -mx-3 sm:mx-0">
                <div className="inline-block min-w-full align-middle px-3 sm:px-0">
                <table
                  ref={(el) => tableRefs.current[weekIndex] = el}
                  className="min-w-full divide-y divide-gray-200"
                >
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32 sm:w-48 sticky left-0 bg-gray-50 z-10">
                        Empleado
                      </th>
                      {weekDays.map((day, dayIndex) => {
                        const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
                        const dayOfWeek = day.getDay();
                        const displayDayName = dayNames[dayOfWeek === 0 ? 6 : dayOfWeek - 1];
                        const isCurrentMonth = day.getMonth() === selectedMonth - 1;
                        return (
                          <th
                            key={dayIndex}
                            className={`px-1 sm:px-2 py-2 sm:py-3 text-center text-xs font-medium uppercase tracking-wider min-w-[64px] sm:min-w-[60px] ${
                              isCurrentMonth ? 'text-gray-500' : 'text-gray-300 bg-gray-100'
                            }`}
                          >
                            {displayDayName}<br/>{day.getDate()}
                          </th>
                        );
                      })}
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16 sm:w-24">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* Row for rooms count - Only for cleaning staff */}
                    {activeTab === USER_ROLES.CLEANING_STAFF && (
                      <tr className="bg-gray-50">
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-900 w-32 sm:w-48 sticky left-0 bg-gray-50 z-10">
                          Habitaciones
                        </td>
                        {weekDays.map((day, dayIndex) => {
                          const isCurrentMonth = day.getMonth() === selectedMonth - 1;
                          return (
                            <td
                              key={dayIndex}
                              className={`px-1 sm:px-2 py-2 text-xs sm:text-sm text-center min-w-[64px] sm:min-w-[60px] ${
                                isCurrentMonth ? 'text-gray-700' : 'text-gray-300 bg-gray-100'
                              }`}
                            >
                              {isCurrentMonth ? getRoomsForDate(day) : '-'}
                            </td>
                          );
                        })}
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm text-center font-semibold text-gray-900 w-16 sm:w-24">
                          {weeklyRoomsTotal}
                        </td>
                      </tr>
                    )}

                    {/* Row for estimated hours - Only for cleaning staff */}
                    {activeTab === USER_ROLES.CLEANING_STAFF && (
                      <tr className="bg-gray-50">
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-900 w-32 sm:w-48 sticky left-0 bg-gray-50 z-10">
                          Horas estimadas
                        </td>
                        {weekDays.map((day, dayIndex) => {
                          const isCurrentMonth = day.getMonth() === selectedMonth - 1;
                          const estimatedHours = parseFloat(getEstimatedHours(day));
                          const staffHours = getStaffHoursForDate(day);
                          const statusColor = isCurrentMonth ? getStaffingStatusColor(day) : '';

                          return (
                            <td
                              key={dayIndex}
                              className={`px-1 sm:px-2 py-2 text-xs sm:text-sm text-center min-w-[64px] sm:min-w-[60px] font-semibold border-2 ${
                                isCurrentMonth ? statusColor : 'text-gray-300 bg-gray-100 border-gray-200'
                              }`}
                              title={isCurrentMonth ? `Personal: ${formatHours(staffHours)}h | Estimado: ${formatHours(estimatedHours)}h` : ''}
                            >
                              {isCurrentMonth ? `${formatHours(estimatedHours)}h` : '-'}
                            </td>
                          );
                        })}
                        <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm text-center font-semibold text-gray-900 w-16 sm:w-24">
                          {formatHours(weeklyEstimatedHoursTotal)}h
                        </td>
                      </tr>
                    )}

                    {/* Row for staff hours */}
                    <tr className="bg-gray-50">
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-900 w-32 sm:w-48 sticky left-0 bg-gray-50 z-10">
                        Horas personal
                      </td>
                      {weekDays.map((day, dayIndex) => {
                        const isCurrentMonth = day.getMonth() === selectedMonth - 1;
                        const staffHours = getStaffHoursForDate(day);

                        return (
                          <td
                            key={dayIndex}
                            className={`px-1 sm:px-2 py-2 text-xs sm:text-sm text-center min-w-[64px] sm:min-w-[60px] ${
                              isCurrentMonth ? 'text-gray-700 font-semibold' : 'text-gray-300 bg-gray-100'
                            }`}
                          >
                            {isCurrentMonth && staffHours > 0 ? `${formatHours(staffHours)}h` : (isCurrentMonth ? '0:00h' : '-')}
                          </td>
                        );
                      })}
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm text-center font-semibold text-gray-900 w-16 sm:w-24">
                        {formatHours(weekDays.reduce((total, day) => {
                          const isCurrentMonth = day.getMonth() === selectedMonth - 1;
                          if (isCurrentMonth) {
                            return total + getStaffHoursForDate(day);
                          }
                          return total;
                        }, 0))}h
                      </td>
                    </tr>

                    {/* Employee rows */}
                    {employees.map((employee, employeeIndex) => {
                      let weeklyTotal = 0;

                      // Get current shift for this employee in this week
                      // Search through all days in the week (current month only) to find the shift
                      let currentShift = '';
                      for (const day of weekDays) {
                        const isCurrentMonth = day.getMonth() === selectedMonth - 1;
                        if (isCurrentMonth) {
                          const dateStr = dateToLocalISO(day);
                          const key = `${employee.id}_${dateStr}`;
                          const dayShift = scheduleData[key]?.shift;
                          if (dayShift) {
                            currentShift = dayShift;
                            break; // Use the first shift found in the week
                          }
                        }
                      }

                      return (
                        <tr key={employee.id}>
                          <td className="px-2 sm:px-4 py-2 w-32 sm:w-48 sticky left-0 bg-white z-10">
                            <div className="flex flex-col space-y-1">
                              <span className="text-xs sm:text-sm font-medium text-gray-900 truncate" title={employee.name}>
                                {employee.name}
                              </span>
                              <select
                                value={currentShift}
                                onChange={(e) => handleShiftChange(employee.id, weekDays, e.target.value)}
                                className="form-select w-full text-xs"
                                disabled={isReadOnly}
                              >
                                <option value="">Sin turno</option>
                                <option value="mañana">Mañana</option>
                                <option value="tarde">Tarde</option>
                                <option value="noche">Noche</option>
                              </select>
                            </div>
                          </td>
                          {weekDays.map((day, dayIndex) => {
                            const dateStr = dateToLocalISO(day);
                            const key = `${employee.id}_${dateStr}`;
                            const hours = scheduleData[key]?.hours || 0;
                            const isCurrentMonth = day.getMonth() === selectedMonth - 1;
                            const cellColors = showScheduleColors && isCurrentMonth
                              ? getEmployeeColorsForDate(employee.id, dateStr)
                              : null;

                            if (isCurrentMonth) {
                              weeklyTotal += hours;
                            }

                            return (
                              <td key={dateStr} className={`px-1 sm:px-2 py-2 min-w-[64px] sm:min-w-[60px] ${!isCurrentMonth ? 'bg-gray-100' : ''}`}>
                                <div className="relative">
                                  <input
                                    type="number"
                                    min="0"
                                    max="24"
                                    step="0.5"
                                    value={isCurrentMonth ? (hours || '') : ''}
                                    onChange={(e) => handleHoursChange(employee.id, dateStr, e.target.value)}
                                    onFocus={handleInputFocus}
                                    onKeyDown={(e) => handleKeyDown(e, weekIndex, employeeIndex, dayIndex, weeks)}
                                    data-week={weekIndex}
                                    data-employee={employeeIndex}
                                    data-day={dayIndex}
                                    tabIndex={isCurrentMonth && !isReadOnly ? 0 : -1}
                                    className={`form-input w-full text-center text-xs sm:text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield] ${cellColors ? 'pb-2.5' : ''}`}
                                    placeholder={isCurrentMonth ? '0' : '-'}
                                    disabled={!isCurrentMonth || isReadOnly}
                                  />
                                  {renderColorStripe(cellColors)}
                                </div>
                              </td>
                            );
                          })}
                          <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm text-center font-semibold text-gray-900 w-16 sm:w-24">
                            {formatHours(weeklyTotal)}h
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          );
        })}
        </div>
      )}

      {/* Legend for staffing status colors - Only for cleaning staff */}
      {activeTab === USER_ROLES.CLEANING_STAFF && (
        <div className="bg-white rounded-lg shadow p-3 sm:p-4">
          <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-3">
            Leyenda de Estado de Personal
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="flex items-center space-x-2">
              <div className="w-12 h-8 bg-red-100 border-2 border-red-200 rounded flex items-center justify-center">
                <span className="text-xs font-semibold text-red-800">5.0h</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Falta Personal</p>
                <p className="text-xs text-gray-600">Las horas de personal son insuficientes para cubrir las horas estimadas de limpieza</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <div className="w-12 h-8 bg-blue-100 border-2 border-blue-200 rounded flex items-center justify-center">
                <span className="text-xs font-semibold text-blue-800">5.0h</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Sobra Personal</p>
                <p className="text-xs text-gray-600">Las horas de personal superan las horas estimadas de limpieza</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <div className="w-12 h-8 bg-gray-50 border-2 border-gray-200 rounded flex items-center justify-center">
                <span className="text-xs font-semibold text-gray-700">5.0h</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Equilibrado</p>
                <p className="text-xs text-gray-600">Las horas de personal y las horas estimadas están equilibradas</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Color legend - Only shown when colors are enabled */}
      {showScheduleColors && colorData && colorData.legend && colorData.legend.length > 0 && (
        <div className="bg-white rounded-lg shadow p-3 sm:p-4">
          <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-3">
            Leyenda de Colores (Grupos de Habitaciones)
          </h3>
          <div className="flex flex-wrap gap-3">
            {/* Free color first */}
            {colorData.free_color && (
              <div className="flex items-center space-x-2">
                <div
                  className="w-6 h-3 rounded border border-gray-300"
                  style={{ backgroundColor: colorData.free_color.color }}
                />
                <span className="text-xs sm:text-sm text-gray-700">
                  {colorData.free_color.name}
                </span>
              </div>
            )}
            {/* Room group colors */}
            {colorData.legend
              .filter(item => !item.is_special_status)
              .map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <div
                    className="w-6 h-3 rounded border border-gray-300"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-xs sm:text-sm text-gray-700">
                    {item.name}
                  </span>
                </div>
              ))
            }
          </div>
        </div>
      )}

      {employees.length === 0 && !isLoading && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-center">
          <p className="text-xs sm:text-sm text-yellow-800">
            {activeTab === USER_ROLES.CLEANING_STAFF && 'No hay personal de limpieza registrado.'}
            {activeTab === USER_ROLES.RECEPTIONIST && 'No hay recepcionistas registrados.'}
            {activeTab === USER_ROLES.MANAGER && 'No hay encargados registrados.'}
            {' '}Por favor, crea empleados primero.
          </p>
        </div>
      )}

      {/* Navigation warning dialog */}
      {showNavigationWarning && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                Cambios sin guardar
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
                Tienes cambios sin guardar en el cuadrante actual. Si continúas, perderás estos cambios.
                ¿Deseas continuar sin guardar?
              </p>
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  onClick={cancelNavigation}
                  className="w-full sm:w-auto px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmNavigation}
                  className="w-full sm:w-auto px-4 py-2 text-xs sm:text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                >
                  Salir sin guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Save Button */}
      {!isReadOnly && (
        <div className={clsx(
          'fixed bottom-8 left-1/2 transform -translate-x-1/2 z-40 transition-all duration-300',
          {
            'opacity-100 translate-y-0': hasUnsavedChanges,
            'opacity-0 translate-y-4 pointer-events-none': !hasUnsavedChanges
          }
        )}>
          <div className="flex items-center gap-2">
            {/* Discard button */}
            <button
              onClick={() => {
                setScheduleData(JSON.parse(JSON.stringify(initialScheduleData)));
                setHasUnsavedChanges(false);
              }}
              className="p-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full shadow-lg transition-all hover:scale-105"
              title="Descartar cambios"
            >
              <FiX className="h-5 w-5" aria-hidden="true" />
            </button>
            {/* Save button */}
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center space-x-3 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full shadow-2xl transition-all hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isSaving ? (
                <>
                  <LoadingSpinner size="sm" color="white" />
                  <span>Guardando...</span>
                </>
              ) : (
                <>
                  <FiSave className="h-5 w-5" aria-hidden="true" />
                  <span>Guardar Cambios</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleManagement;
